import { formatApiErrorDetail } from "@/lib/api";
/**
 * LandlordQuoteApproval — public magic-link page where a landlord reviews
 * and signs a repair quote. NO login required. Available at
 * `/quotes/approve/:token`. Tracks view-time, captures typed/drawn signature,
 * and confirms AppFolio funding.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, XCircle, PencilSimple, TextT, FilePdf, Image as ImageIcon } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandlordQuoteApproval() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("typed"); // typed | drawn
  const [typedName, setTypedName] = useState("");
  const [confirmFund, setConfirmFund] = useState(true);
  const [busy, setBusy] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showDecline, setShowDecline] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/landlord-quotes/${token}`);
      setQ(r.data);
      // Mark viewed (best-effort)
      axios.post(`${API}/public/landlord-quotes/${token}/view`).catch(() => {});
    } catch (e) {
      setErr(e.response?.data?.detail || "Quote not found");
    }
  };
  useEffect(() => { load(); }, [token]);

  // Signature pad handlers
  const canvasPoint = (e) => {
    const c = canvasRef.current;
    if (!c) return null;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const p = canvasPoint(e);
    if (!p) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
  };
  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    const p = canvasPoint(e);
    if (!p) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const endDraw = () => { drawingRef.current = false; };
  const clearPad = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const sign = async () => {
    if (!confirmFund) return toast.error("Please confirm AppFolio funding to authorize");
    let payload = { signature_type: mode, confirm_fund: confirmFund, typed_name: typedName };
    if (mode === "drawn") {
      const c = canvasRef.current;
      if (!c) return toast.error("Signature pad missing");
      // Detect empty canvas
      const ctx = c.getContext("2d");
      const pixels = ctx.getImageData(0, 0, c.width, c.height).data;
      const hasInk = Array.from(pixels).some((v, i) => i % 4 === 3 && v > 0);
      if (!hasInk) return toast.error("Draw your signature first");
      payload.drawn_data_url = c.toDataURL("image/png");
      if (!payload.typed_name) payload.typed_name = q?.landlord_name || "Owner";
    } else {
      if ((typedName || "").trim().length < 2) return toast.error("Type your full name to sign");
    }
    setBusy(true);
    const tId = toast.loading("Recording your signature…");
    try {
      await axios.post(`${API}/public/landlord-quotes/${token}/sign`, payload);
      toast.success("Signed! We'll dispatch as soon as funds clear.", { id: tId });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Signing failed"), { id: tId });
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/landlord-quotes/${token}/decline`, {
        reason: declineReason,
      });
      toast.success("Quote declined — we've notified the office.");
      await load();
      setShowDecline(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Decline failed"));
    } finally { setBusy(false); }
  };

  const total = q?.landlord_total_cents ?? 0;
  const fmt = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const brand = q?.brand_color || "#0f172a";
  const addressLine = q ? `${q.address}${q.unit ? " · Unit " + q.unit : ""}` : "";
  const pdfUrl = `${API}/public/landlord-quotes/${token}/pdf`;

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center rugged-card p-8" data-testid="lq-error">
          <XCircle weight="fill" size={56} className="text-red-500 mx-auto mb-3"/>
          <h1 className="font-display font-black text-2xl">Quote Not Available</h1>
          <p className="mt-2 text-slate-600">{err}</p>
        </div>
      </div>
    );
  }
  if (!q) return <div className="min-h-screen bg-slate-50 flex items-center justify-center">Loading…</div>;

  const isSigned = q.status === "signed";
  const isDeclined = q.status === "declined";

  return (
    <div className="min-h-screen bg-slate-100" data-testid="lq-approval-page">
      {/* Hero */}
      <div style={{ background: brand }} className="text-white py-8 px-5">
        <div className="max-w-2xl mx-auto">
          <div className="text-[10px] font-black uppercase tracking-[.22em] opacity-70">
            {q.tenant_name} · Repair Quote &amp; Authorization
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-black leading-tight">
            {addressLine}
          </h1>
          <div className="mt-1 opacity-80 text-sm">{q.title}</div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-5 space-y-5">
        {/* Total card */}
        <div className="bg-white border-4 border-[#0f172a] p-6 shadow-[6px_6px_0_#0f172a]">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total due</div>
          <div className="mt-1 text-5xl sm:text-6xl font-black" style={{ color: brand }}
               data-testid="lq-approval-total">
            {fmt(total)}
          </div>
          <div className="mt-1 text-sm text-slate-600">All-inclusive · labor, materials, cleanup</div>
        </div>

        {/* Scope */}
        <div className="bg-white border-2 border-slate-200 p-6">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">
            Scope of Work
          </div>
          <div className="whitespace-pre-wrap text-slate-800 leading-relaxed text-[15px]"
               data-testid="lq-approval-scope">
            {q.scope}
          </div>
          <a href={pdfUrl} target="_blank" rel="noreferrer"
             data-testid="lq-approval-download-pdf"
             className="mt-4 inline-flex items-center gap-2 h-10 px-4 border-2 border-[#0f172a]
                        bg-slate-50 hover:bg-slate-100 font-black uppercase text-xs tracking-widest">
            <FilePdf weight="fill" size={16}/> Download PDF
          </a>
        </div>

        {/* Reference photos */}
        {(q.photo_urls || []).length > 0 && (
          <div className="bg-white border-2 border-slate-200 p-6" data-testid="lq-approval-photos">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5">
              <ImageIcon weight="fill" size={12}/> Reference Photos ({q.photo_urls.length})
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {q.photo_urls.map((relUrl, i) => (
                <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${relUrl}`} target="_blank" rel="noreferrer"
                   className="aspect-[4/3] block border-2 border-slate-300 overflow-hidden hover:border-[#0f172a] transition-colors">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${relUrl}`}
                       alt={`Reference ${i + 1}`}
                       data-testid={`lq-approval-photo-${i}`}
                       className="w-full h-full object-cover"/>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Terms */}
        <div className="bg-slate-50 border-2 border-slate-300 p-5 text-sm text-slate-700 leading-relaxed">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
            Terms
          </div>
          <ul className="list-disc pl-4 space-y-1">
            <li>Approval authorizes {q.tenant_name} to schedule and complete the work above.</li>
            <li>Any hidden damage discovered mid-work will be quoted as a change order — nothing extra without your OK.</li>
            <li>Permits are owner responsibility unless expressly included.</li>
            <li>Payment on completion. Funds must be available in your AppFolio portfolio before we dispatch.</li>
            <li>30-day workmanship warranty. Materials carry manufacturer warranties.</li>
          </ul>
        </div>

        {/* Signed / declined banner */}
        {isSigned && (
          <div className="bg-emerald-50 border-4 border-emerald-500 p-6 text-center"
               data-testid="lq-signed-state">
            <CheckCircle weight="fill" size={48} className="text-emerald-600 mx-auto"/>
            <h2 className="mt-2 text-2xl font-black text-emerald-900">Signed &amp; Authorized</h2>
            <p className="mt-1 text-emerald-800">
              Signed by <b>{q.signed_by_name}</b> at {q.signed_at ? new Date(q.signed_at).toLocaleString() : ""}.
            </p>
            <p className="mt-1 text-sm text-emerald-700">
              We'll dispatch as soon as your AppFolio portfolio is funded. Thanks!
            </p>
          </div>
        )}

        {isDeclined && (
          <div className="bg-rose-50 border-4 border-rose-500 p-6 text-center"
               data-testid="lq-declined-state">
            <XCircle weight="fill" size={48} className="text-rose-600 mx-auto"/>
            <h2 className="mt-2 text-2xl font-black text-rose-900">Quote Declined</h2>
            {q.decline_reason && <p className="mt-1 text-rose-800 text-sm">Reason: {q.decline_reason}</p>}
            <p className="mt-2 text-sm text-rose-700">
              Reach out to the office if you'd like to revise or resubmit.
            </p>
          </div>
        )}

        {/* Signature panel */}
        {!isSigned && !isDeclined && (
          <div className="bg-white border-4 border-yellow-500 p-6 shadow-[6px_6px_0_#facc15]">
            <div className="text-[10px] font-black uppercase tracking-widest text-yellow-800 mb-3">
              Authorize &amp; Sign
            </div>
            {/* AppFolio confirm */}
            <label className="flex items-start gap-2 text-sm mb-4 cursor-pointer">
              <input type="checkbox" checked={confirmFund}
                     onChange={(e) => setConfirmFund(e.target.checked)}
                     data-testid="lq-confirm-fund"
                     className="mt-0.5 h-5 w-5 border-2 border-[#0f172a]"/>
              <span>
                <b>I authorize this repair</b> and confirm funds will be available in my AppFolio portfolio
                before dispatch.
                {q.appfolio_funding_url && (
                  <> <a href={q.appfolio_funding_url} target="_blank" rel="noreferrer"
                        className="font-black text-cyan-700 underline">Open my AppFolio →</a></>
                )}
              </span>
            </label>

            {/* Signature mode picker */}
            <div className="flex gap-2 mb-3">
              <button onClick={() => setMode("typed")}
                      data-testid="lq-mode-typed"
                      className={`flex-1 h-11 px-3 font-black uppercase text-xs tracking-widest border-2 inline-flex items-center justify-center gap-2
                        ${mode === "typed" ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-[#0f172a] border-[#0f172a]"}`}>
                <TextT weight="bold" size={14}/> Type Name
              </button>
              <button onClick={() => setMode("drawn")}
                      data-testid="lq-mode-drawn"
                      className={`flex-1 h-11 px-3 font-black uppercase text-xs tracking-widest border-2 inline-flex items-center justify-center gap-2
                        ${mode === "drawn" ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-[#0f172a] border-[#0f172a]"}`}>
                <PencilSimple weight="bold" size={14}/> Draw Signature
              </button>
            </div>

            {mode === "typed" && (
              <div>
                <label className="text-xs font-black uppercase tracking-widest text-slate-500">Your full legal name</label>
                <input value={typedName} onChange={(e) => setTypedName(e.target.value)}
                       placeholder="e.g., Muhammad Raza"
                       data-testid="lq-typed-name"
                       className="mt-1 w-full h-12 px-3 border-2 border-[#0f172a] font-medium"/>
                {typedName.trim() && (
                  <div className="mt-2 p-3 bg-slate-50 border-2 border-slate-200">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Signature preview</div>
                    <div className="mt-1 text-2xl italic" style={{ fontFamily: "'Brush Script MT', cursive" }}>
                      {typedName}
                    </div>
                  </div>
                )}
              </div>
            )}

            {mode === "drawn" && (
              <div>
                <label className="text-xs font-black uppercase tracking-widest text-slate-500">
                  Draw with finger or mouse
                </label>
                <div className="mt-1 border-2 border-[#0f172a] bg-white touch-none"
                     onMouseDown={startDraw} onMouseMove={moveDraw}
                     onMouseUp={endDraw} onMouseLeave={endDraw}
                     onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}>
                  <canvas ref={canvasRef} width={520} height={160}
                          data-testid="lq-signature-canvas"
                          className="w-full h-40 block"/>
                </div>
                <div className="mt-2 flex justify-between items-center gap-2">
                  <button onClick={clearPad} type="button"
                          data-testid="lq-signature-clear"
                          className="h-8 px-3 border-2 border-slate-400 bg-white font-black uppercase text-[11px] tracking-widest text-slate-600">
                    Clear
                  </button>
                  <div>
                    <input value={typedName} onChange={(e) => setTypedName(e.target.value)}
                           placeholder="Print your name below the signature"
                           data-testid="lq-drawn-print-name"
                           className="h-9 px-2 border-2 border-slate-300 text-xs font-medium w-56"/>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2">
              <button onClick={() => setShowDecline(true)} disabled={busy}
                      data-testid="lq-decline-open"
                      className="h-12 px-4 border-2 border-rose-500 bg-white text-rose-700 font-black uppercase text-xs tracking-widest hover:bg-rose-50">
                Decline Quote
              </button>
              <button onClick={sign} disabled={busy}
                      data-testid="lq-sign"
                      className="flex-1 h-12 bg-yellow-500 hover:bg-yellow-600 text-[#0f172a] font-black uppercase text-sm tracking-widest
                                 inline-flex items-center justify-center gap-2 disabled:opacity-60 shadow-[4px_4px_0_#0f172a]">
                <CheckCircle weight="fill" size={18}/>
                {busy ? "Signing…" : "Approve & Sign"}
              </button>
            </div>
          </div>
        )}

        {/* Decline modal */}
        {showDecline && !isSigned && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
               onClick={() => setShowDecline(false)}>
            <div className="bg-white max-w-md w-full border-4 border-rose-500 p-5"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="lq-decline-modal">
              <h2 className="font-black text-xl">Decline this quote?</h2>
              <p className="mt-1 text-sm text-slate-600">
                Optional — let us know what to change so we can re-quote quickly.
              </p>
              <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                        rows={4} placeholder="Reason (optional)"
                        data-testid="lq-decline-reason"
                        className="mt-3 w-full p-3 border-2 border-slate-300 text-sm"/>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setShowDecline(false)}
                        className="flex-1 h-10 border-2 border-slate-400 bg-white font-black uppercase text-xs tracking-widest">
                  Cancel
                </button>
                <button onClick={decline} disabled={busy}
                        data-testid="lq-decline-confirm"
                        className="flex-1 h-10 bg-rose-500 hover:bg-rose-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-60">
                  {busy ? "Sending…" : "Decline Quote"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="text-center py-4 text-[11px] text-slate-500 font-medium">
          Powered by {q.tenant_name} · Repair Quote v1
        </div>
      </div>
    </div>
  );
}
